import { createPersistedStore } from '../../../shared/stores/create-persisted-store'
import { clearAllSiteData } from '../../../shared/lib/clear-all-site-data'
import type {
  CurrentUser,
  AuthUser,
  RoleItem,
  PermissionMap,
  ViewPermission,
  ViewItems,
} from './types'

/** Read a cookie value by name (used to hydrate token on startup) */
function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'))
  // invariant: regex has a capturing group, so match[1] exists when match is non-null
  return match ? decodeURIComponent(match[1]!) : null
}

export type { PermissionActionFlags as PermissionAction } from './types'
export type { PermissionMap, ViewPermission, ViewItems } from './types'
export type PermissionGroup = Record<string, import('./types').PermissionActionFlags>

export interface AuthState {
  users: string[]
  token: string | null
  phone: string
  pageSize: string
  role: RoleItem[]
  roles: RoleItem[]
  currentUser: CurrentUser
  permission: PermissionMap
  user: AuthUser
  viewPermission: ViewPermission
  viewItems: ViewItems
  preLoader: boolean
  sideBar: boolean
  isImageChanged: boolean
  isAuthenticationComplete: boolean

  login: (token: string) => void
  logout: () => void
  setRole: (role: RoleItem[]) => void
  setUserRoles: (roles: RoleItem[]) => void
  setToken: (token: string) => void
  setCurrentUser: (user: CurrentUser) => void
  setPageSize: (size: string) => void
  setPermissions: (permissions: PermissionMap) => void
  setPreLoader: (loading: boolean) => void
  setUser: (user: AuthUser) => void
  setEmail: (email: string) => void
  setPhone: (phone: string) => void
  setSideBar: (visible: boolean) => void
  setViewPermission: (permission: ViewPermission) => void
  setViewItems: (items: ViewItems) => void
  setIsImageChanged: (changed: boolean) => void
  setAuthenticationComplete: (complete: boolean) => void
}

const initialState = {
  users: ['admin'] as string[],
  token: readCookie('token') as string | null,
  phone: '',
  pageSize: '20',
  role: [] as RoleItem[],
  roles: [] as RoleItem[],
  currentUser: {} as CurrentUser,
  permission: {} as PermissionMap,
  user: {} as AuthUser,
  viewPermission: {} as ViewPermission,
  viewItems: {} as ViewItems,
  preLoader: false,
  sideBar: false,
  isImageChanged: false,
  isAuthenticationComplete: false,
}

/**
 * Auth store mixes credential-shaped state (token, permissions, user, roles)
 * with a single UI field (`sideBar`). Persisting the whole store to
 * localStorage would leak credentials and violate CLAUDE.md hard rule #5.
 *
 * `partialize` is therefore a SECURITY boundary — only `sideBar` reaches
 * localStorage. Tokens stay cookie-based; permissions/user re-hydrate from
 * the `/me` endpoint on every page load via `useUserMe`.
 */
export const useAuthStore = createPersistedStore<AuthState, { sideBar: boolean }>(
  (set) => ({
    ...initialState,

    login: (token: string) =>
      set((state) => {
        state.token = token
      }),

    logout: () => {
      // Wave V extracted clearAllSiteData() to shared/lib but didn't wire
      // it back here — Wave Y closes that regression. Every logout path
      // (request.ts 401 handler, user-popup-modal, future ones) now
      // gets the full browser-storage purge for free, matching the
      // pre-Wave-E behavior. Side-effect-coupled by design: a logout
      // that doesn't clear storage isn't a logout.
      clearAllSiteData()
      set((state) => {
        Object.assign(state, initialState, { token: null })
      })
    },

    setRole: (role: RoleItem[]) =>
      set((state) => {
        state.role = role
      }),

    setUserRoles: (roles: RoleItem[]) =>
      set((state) => {
        state.roles = roles
      }),

    setToken: (token: string) =>
      set((state) => {
        state.token = token
      }),

    setCurrentUser: (user: CurrentUser) =>
      set((state) => {
        state.currentUser = user
      }),

    setPageSize: (size: string) =>
      set((state) => {
        state.pageSize = size
      }),

    setPermissions: (permissions: PermissionMap) =>
      set((state) => {
        state.permission = permissions
        state.isAuthenticationComplete = true
      }),

    setPreLoader: (loading: boolean) =>
      set((state) => {
        state.preLoader = loading
      }),

    setUser: (user: AuthUser) =>
      set((state) => {
        state.user = user
      }),

    setEmail: (email: string) =>
      set((state) => {
        state.currentUser.email = email
      }),

    setPhone: (phone: string) =>
      set((state) => {
        state.phone = phone
      }),

    setSideBar: (visible: boolean) =>
      set((state) => {
        state.sideBar = visible
      }),

    setViewPermission: (permission: ViewPermission) =>
      set((state) => {
        state.viewPermission = permission
      }),

    setViewItems: (items: ViewItems) =>
      set((state) => {
        state.viewItems = items
      }),

    setIsImageChanged: (changed: boolean) =>
      set((state) => {
        state.isImageChanged = changed
      }),

    setAuthenticationComplete: (complete: boolean) =>
      set((state) => {
        state.isAuthenticationComplete = complete
      }),
  }),
  {
    name: 'glasspro:auth-ui',
    version: 1,
    // SECURITY-CRITICAL — see store-level docblock. NEVER expand this set
    // without re-reading CLAUDE.md hard rule #5.
    partialize: (state) => ({ sideBar: state.sideBar }),
  },
)
